import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  amountWithCurrencySymbol,
  mapLocaleCurrencyCode,
} from '@/helpers/currency';
import {
  convertAmount,
  findNearestStake,
  JACKPOT_CURRENCY,
  toSupportedCurrency,
} from '@/helpers/jackpot';
import { useGetCommonStore } from '@/store';

export const useExchangeAmount = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const useCommonStore = useGetCommonStore();
  const user = useCommonStore.use.user();
  const exchangeRates = useCommonStore.use.exchangeRates();

  const displayCurrency = toSupportedCurrency(
    user ? user.currency : mapLocaleCurrencyCode[language]
  );

  const getExchangeAmount = useCallback(
    (amount: number, roundUp = false): number => {
      if (!exchangeRates || displayCurrency === JACKPOT_CURRENCY) {
        return amount;
      }

      const rate =
        exchangeRates.fiat?.[JACKPOT_CURRENCY]?.[displayCurrency]
          ?.exchangeRate ?? 1;

      return convertAmount(amount, rate, roundUp);
    },
    [exchangeRates, displayCurrency]
  );

  const formatNumber = useCallback(
    (amount: number, fractionDigits?: number): string =>
      amount.toLocaleString(language, {
        minimumFractionDigits: fractionDigits ?? 0,
        maximumFractionDigits: fractionDigits ?? 2,
      }),
    [language]
  );

  const formatAmount = useCallback(
    (amount: number, fractionDigits?: number): string =>
      amountWithCurrencySymbol(
        displayCurrency,
        formatNumber(amount, fractionDigits),
        { noDecimals: true }
      ),
    [displayCurrency, formatNumber]
  );

  const formatExchangedAmount = useCallback(
    (amount: number, fractionDigits?: number): string =>
      formatAmount(getExchangeAmount(amount), fractionDigits),
    [getExchangeAmount, formatAmount]
  );

  const formatMinBet = useCallback(
    (minBet: number): string =>
      formatAmount(
        findNearestStake(getExchangeAmount(minBet, true), displayCurrency)
      ),
    [getExchangeAmount, displayCurrency, formatAmount]
  );

  return {
    displayCurrency,
    getExchangeAmount,
    formatNumber,
    formatAmount,
    formatExchangedAmount,
    formatMinBet,
  };
};
